//@ts-ignore
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import moment from "moment-timezone";
import styled, { css } from "styled-components";

import Input from "../ui-kit/input";
import { Button, Frame, RowWrapper } from "../ui-kit/styled-templates";
import Calendar from "./calendar";

const months = (t: any) => [
    t("ebah15zzqyr"),
    t("mk211mz71s"),
    t("52oy8yea6cf"),
    t("60j2z1zh29w"),
    t("q77husf8zh"),
    t("f13vy5d4m5j"),
    t("4dx2zvtkcui"),
    t("3at759zqpnf"),
    t("74wrdmtyr15"),
    t("8yrlf0uwcsn"),
    t("wwstfqt0zfk"),
    t("jqv0yg8xvun"),
];

// Определяем, мобильное ли устройство
const isMobileDevice = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches ||
           'ontouchstart' in window ||
           navigator.maxTouchPoints > 0;
};

type HoverDatePickerProps = {
    onChange: Function;
    value: any;
    toggleComponent?: any;
    withTime?: boolean;
    saveOnSelect?: boolean;
    placeholder?: string;
};

const HoverDatePicker = (props: HoverDatePickerProps) => {
    const { t, i18n } = useTranslation();
    const isRu = i18n.language === 'ru';

    const { onChange, value, toggleComponent: ToggleComponent, withTime = false, saveOnSelect = true, placeholder } = props;

    const [monthStart, setMonthStart] = useState(moment().startOf("month"));
    const [selectFrom, setSelectFrom] = useState<any>(null);
    const [timeFrom, setTimeFrom] = useState(`00:00`);
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [inputValue, setInputValue] = useState(''); // Текстовое значение для ручного ввода

    const wrapperRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const userClickedRef = useRef(false);

    // Определяем мобильное устройство при монтировании
    useEffect(() => {
        setIsMobile(isMobileDevice());

        const handleResize = () => {
            setIsMobile(isMobileDevice());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handlers = {
        prevMonth: () => {
            setMonthStart(monthStart.clone().subtract(1, "month"));
        },
        nextMonth: () => {
            setMonthStart(monthStart.clone().add(1, "month"));
        },
        dateClick: (date: any) => {
            userClickedRef.current = true;
            setSelectFrom(date);
        },
        cancelClick: () => {
            setSelectFrom(null);
            setTimeFrom(`00:00`);
        },
        saveClick: () => {
            onChange({
                target: {
                    value: +selectFrom.clone().set({ hour: timeFrom.split(":")[0], minute: timeFrom.split(":")[1] }),
                },
            });
            setIsOpen(false);
        },
    };

    // Синхронизация с внешним value
    useEffect(() => {
        if (value && !isNaN(+value)) {
            setSelectFrom(moment(+value));
            setTimeFrom(moment(+value).format("HH:mm"));
            setInputValue(moment(+value).format(withTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY'));
        } else {
            setInputValue('');
        }
    }, [value, withTime]);

    // Автоформатирование ввода даты: только цифры, автоматические точки
    const formatDateInput = (text: string, prevText: string): string => {
        // Убираем все кроме цифр
        const digitsOnly = text.replace(/\D/g, '');

        // Ограничиваем до 8 цифр (DDMMYYYY)
        const limited = digitsOnly.slice(0, 8);

        // Добавляем точки после 2-й и 4-й цифры
        let formatted = '';
        for (let i = 0; i < limited.length; i++) {
            if (i === 2 || i === 4) {
                formatted += '.';
            }
            formatted += limited[i];
        }

        return formatted;
    };

    // Парсинг введённой даты
    const parseInputDate = (text: string): moment.Moment | null => {
        // Поддерживаемые форматы: DD.MM.YYYY
        const formats = ['DD.MM.YYYY'];
        const parsed = moment(text, formats, true);
        if (parsed.isValid()) {
            return parsed;
        }
        return null;
    };

    // Обработка ввода с клавиатуры
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawText = e.target.value;

        // Автоформатируем ввод
        const formatted = formatDateInput(rawText, inputValue);
        setInputValue(formatted);

        // Пытаемся распарсить дату (только если полный формат DD.MM.YYYY)
        if (formatted.length === 10) {
            const parsed = parseInputDate(formatted);
            if (parsed) {
                setSelectFrom(parsed);
                setMonthStart(parsed.clone().startOf('month'));

                // Если saveOnSelect, применяем дату сразу
                if (saveOnSelect) {
                    onChange({
                        target: {
                            value: +parsed.clone().set({ hour: parseInt(timeFrom.split(":")[0], 10), minute: parseInt(timeFrom.split(":")[1], 10) }),
                        },
                    });
                }
            }
        }
    };

    // Обработка потери фокуса - форматируем дату
    const handleInputBlur = () => {
        if (value && !isNaN(+value)) {
            setInputValue(moment(+value).format(withTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY'));
        } else {
            // Если введённый текст не парсится в валидную дату, очищаем
            const parsed = parseInputDate(inputValue);
            if (!parsed) {
                setInputValue('');
            }
        }
    };

    // Открытие календаря при фокусе на инпут
    const handleInputFocus = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsOpen(true);
    };

    // Автосохранение при клике пользователя (saveOnSelect режим)
    useEffect(() => {
        if (userClickedRef.current && selectFrom && saveOnSelect && !isNaN(+selectFrom)) {
            userClickedRef.current = false;
            handlers.saveClick();
        }
    }, [selectFrom]);

    // Обработчики для hover (desktop)
    const handleMouseEnter = () => {
        if (isMobile) return;

        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        if (isMobile) return;

        closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 150);
    };

    // Обработчик клика (для мобильных)
    const handleClick = () => {
        if (isMobile) {
            setIsOpen(!isOpen);
        }
    };

    // Закрытие при клике вне компонента
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Очистка таймера при размонтировании
    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    return (
        <Wrapper
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Toggle onClick={handleClick}>
                {ToggleComponent ? (
                    <ToggleComponent {...{ value, onChange }} />
                ) : (
                    <InputWrapper>
                        <StyledInput
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            placeholder={placeholder || (isRu ? 'ДД.ММ.ГГГГ' : 'DD.MM.YYYY')}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                        />
                        <CalendarIcon />
                    </InputWrapper>
                )}
            </Toggle>

            <Menu ref={menuRef} visible={isOpen}>
                <Frame extra={({ theme }: any) => `background: ${theme.background.primary}; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15);`}>
                    <Frame extra={({ theme }: any) => `flex-direction: row; ${withTime ? `border-bottom: 1px solid ${theme.grey};` : ''}`}>
                        <Frame extra={`padding: 19px;`}>
                            <Frame extra={`width: 100%; flex-direction: row; justify-content: space-between;`}>
                                <Chevron direction="left" onClick={handlers.prevMonth} />
                                <Frame extra={`font-size: 13px; line-height: 18px;`}>
                                    {months(t)[monthStart.month()]} {` ${moment(monthStart).format('YYYY')}`}
                                </Frame>
                                <Chevron direction="right" onClick={handlers.nextMonth} />
                            </Frame>
                            <Calendar
                                monthStart={monthStart}
                                onDateClick={handlers.dateClick}
                                selectedRange={[selectFrom, selectFrom]}
                                showOnlyCurrentMonth={true}
                            />
                        </Frame>
                    </Frame>
                    {withTime && (
                        <RowWrapper extra={`padding: 19px; box-sizing: border-box;`}>
                            <Frame extra={`flex-direction: row;`}>
                                <Input type="time" value={timeFrom} onChange={(e: any) => setTimeFrom(e.target.value)} />
                            </Frame>
                            <Frame extra={`flex-direction: row;`}>
                                <Button
                                    background="white"
                                    extra={({ theme }: any) =>
                                        `margin-right: 1em; min-width: unset; font-size: 12px; line-height: 16px; border: 1px solid ${theme.grey}; color: ${theme.grey}; box-shadow: none;`
                                    }
                                    onClick={handlers.cancelClick}
                                >
                                    {t("cs102zu7vsd")}
                                </Button>
                                <Button
                                    disabled={!selectFrom}
                                    background="green"
                                    extra={`min-width: unset; font-size: 12px; line-height: 16px; box-shadow: none;`}
                                    onClick={handlers.saveClick}
                                >
                                    {t("9iheqfunzms")}
                                </Button>
                            </Frame>
                        </RowWrapper>
                    )}
                </Frame>
            </Menu>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    position: relative;
    width: 100%;
`;

const Toggle = styled.div`
    cursor: pointer;
    width: 100%;
`;

const InputWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 10px 35px 10px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 14px;
    background: white;
    box-sizing: border-box;

    &:hover {
        border-color: #bdbdbd;
    }

    &:focus {
        outline: none;
        border-color: #02BF89;
    }

    &::placeholder {
        color: #9e9e9e;
    }
`;

const CalendarIcon = styled(Frame)`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    background: url("${require(`../../assets/icons/calendar.svg`).default}") no-repeat center center / contain;
    pointer-events: none;
`;

const Menu = styled.div<{ visible: boolean }>`
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    margin-top: 4px;
    display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

const Chevron = styled(Frame)<{ direction: `left` | `right` }>`
    width: 28px;
    height: 28px;
    border-radius: 4px;
    box-sizing: border-box;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }

    &:after {
        content: "";
        width: 12px;
        height: 12px;
        background: url("${require(`../../assets/icons/arrow-right.svg`).default}") no-repeat center center / contain;
        ${({ direction }) =>
            direction === "left" &&
            css`
                transform: rotate(180deg);
            `}
    }
`;

export default HoverDatePicker;
